import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { RefreshCw, Calendar, Wallet, MapPin, Users, ChevronDown, ChevronRight } from 'lucide-react';

const METHOD_LABELS = {
  fr: {
    cash: 'Espèces',
    virement: 'Virement',
    mobile_money: 'Mobile Money',
    carte: 'Carte',
    non_renseigne: 'Non renseigné',
  },
  en: {
    cash: 'Cash',
    virement: 'Transfer',
    mobile_money: 'Mobile Money',
    carte: 'Card',
    non_renseigne: 'Not specified',
  },
};

const METHOD_COLORS = {
  cash: '#E8721A',
  virement: '#3B82F6',
  mobile_money: '#10B981',
  carte: '#8B5CF6',
  non_renseigne: '#9CA3AF',
};

const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const PaymentMethodChart = () => {
  const { t, language } = useLanguage();
  const labels = METHOD_LABELS[language] || METHOD_LABELS.fr;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedShipment, setSelectedShipment] = useState('');
  const [selectedPartner, setSelectedPartner] = useState('');
  const [shipments, setShipments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMethod, setExpandedMethod] = useState(null);

  // Fetch shipments and partners lists for dropdowns
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const res = await api.get('/api/shipments');
        const list = Array.isArray(res.data) ? res.data : res.data.shipments || [];
        setShipments(list);
      } catch {}
    };
    const fetchPartners = async () => {
      try {
        const res = await api.get('/api/users?role=partner');
        setPartners(Array.isArray(res.data) ? res.data : []);
      } catch {}
    };
    fetchShipments();
    fetchPartners();
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedPartner) params.append('partner_id', selectedPartner);
      const url = selectedShipment
        ? `/api/finance/shipments/${selectedShipment}/payment-stats?${params.toString()}`
        : `/api/finance/payment-stats?${params.toString()}`;
      const res = await api.get(url);
      setData(res.data);
    } catch (err) {
      console.error('Payment stats error:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedShipment, selectedPartner]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Quick period presets
  const setPeriod = (days) => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    setDateFrom(from);
    setDateTo(to);
  };

  const setThisYear = () => {
    const year = new Date().getFullYear();
    setDateFrom(`${year}-01-01`);
    setDateTo(new Date().toISOString().slice(0, 10));
  };

  const methodData = data?.by_method
    ? Object.entries(data.by_method)
        .map(([key, val]) => ({
          key,
          name: labels[key] || key,
          count: val.count,
          amount: val.total_amount,
          pct: data.total_revenue > 0 ? ((val.total_amount / data.total_revenue) * 100).toFixed(1) : '0.0',
          color: METHOD_COLORS[key] || '#9CA3AF',
        }))
        .sort((a, b) => b.amount - a.amount)
    : [];

  const statusData = data?.payment_status_breakdown
    ? [
        { name: language === 'en' ? 'Paid' : 'Payé', value: data.payment_status_breakdown.paid || 0, color: '#10B981' },
        { name: language === 'en' ? 'Unpaid' : 'Impayé', value: data.payment_status_breakdown.unpaid || 0, color: '#EF4444' },
        { name: language === 'en' ? 'Advance' : 'Acompte', value: data.payment_status_breakdown.advance || 0, color: '#F59E0B' },
      ].filter((d) => d.value > 0)
    : [];

  const pieData = methodData
    .filter((d) => d.amount > 0)
    .map((d) => ({ name: d.name, value: d.amount, color: d.color }));

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wallet size={18} />
            {language === 'en' ? 'Payment methods analysis' : 'Analyse des moyens de paiement'}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Date filters */}
        <div className="flex items-end gap-3 flex-wrap mb-6">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar size={12} />
              {language === 'en' ? 'From' : 'Du'}
            </Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full sm:w-40 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar size={12} />
              {language === 'en' ? 'To' : 'Au'}
            </Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full sm:w-40 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {language === 'en' ? 'Departure' : 'Départ'}
            </Label>
            <select
              value={selectedShipment}
              onChange={(e) => setSelectedShipment(e.target.value)}
              className="border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full sm:min-w-[180px]"
            >
              <option value="">{language === 'en' ? 'All departures' : 'Tous les départs'}</option>
              {shipments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.reference || s.id}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Users size={12} />
              {language === 'en' ? 'Partner' : 'Partenaire'}
            </Label>
            <select
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              className="border border-border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring w-full sm:min-w-[180px]"
            >
              <option value="">{language === 'en' ? 'All partners' : 'Tous les partenaires'}</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.email}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPeriod(7)}>7j</Button>
            <Button variant="outline" size="sm" onClick={() => setPeriod(30)}>30j</Button>
            <Button variant="outline" size="sm" onClick={() => setPeriod(90)}>90j</Button>
            <Button variant="outline" size="sm" onClick={setThisYear}>
              {language === 'en' ? 'This year' : 'Année'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-12">{t('loading')}</div>
        ) : !data ? (
          <div className="text-center text-muted-foreground py-12">{t('noData')}</div>
        ) : (
          <>
            {/* KPI summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="border border-border rounded-lg p-3 bg-muted">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{language === 'en' ? 'Total revenue' : 'CA total'}</div>
                <div className="text-lg font-bold text-foreground mt-1">{fmt(data.total_revenue)}</div>
              </div>
              <div className="border border-border rounded-lg p-3 bg-muted">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{language === 'en' ? 'Packages' : 'Colis'}</div>
                <div className="text-lg font-bold text-foreground mt-1">{data.total_packages}</div>
              </div>
              <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{language === 'en' ? 'Paid' : 'Payés'}</div>
                <div className="text-lg font-bold text-green-700 mt-1">{data.payment_status_breakdown?.paid || 0}</div>
              </div>
              <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{language === 'en' ? 'Unpaid amount' : 'Montant impayé'}</div>
                <div className="text-lg font-bold text-red-700 mt-1">{fmt(data.unpaid_total)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar chart: amount per method */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  {language === 'en' ? 'Revenue by payment method' : 'CA par moyen de paiement'}
                </h4>
                {methodData.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">{t('noData')}</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={methodData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#8E8A82' }} tickFormatter={(v) => `${v}€`} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12, fill: '#8E8A82' }} />
                      <Tooltip
                        formatter={(value) => [fmt(value)]}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }}
                      />
                      <Bar dataKey="amount" radius={[0, 4, 4, 0]} barSize={24}>
                        {methodData.map((entry) => (
                          <Cell key={entry.key} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Pie chart: % share */}
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  {language === 'en' ? 'Share of revenue' : 'Part du CA'}
                </h4>
                {pieData.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">{t('noData')}</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [fmt(value)]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-3 mt-2">
                      {pieData.map((d, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs">
                          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
                          <span className="text-muted-foreground">{d.name}</span>
                          <span className="font-medium text-foreground">{fmt(d.value)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Detail table */}
            {methodData.length > 0 && (
              <div className="mt-6 border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Method' : 'Moyen'}</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Count' : 'Nombre'}</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Amount' : 'Montant'}</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">% CA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {methodData.map((m) => {
                      const isExpanded = expandedMethod === m.key;
                      const packages = data?.packages_by_method?.[m.key] || [];
                      return (
                        <React.Fragment key={m.key}>
                          <tr
                            className="hover:bg-muted/30 cursor-pointer"
                            onClick={() => setExpandedMethod(isExpanded ? null : m.key)}
                          >
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: m.color }} />
                                <span className="font-medium text-foreground">{m.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-right text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {m.count}
                            </td>
                            <td className="px-4 py-2.5 text-right font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(m.amount)}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <Badge variant="outline" className="text-[10px]">{m.pct}%</Badge>
                            </td>
                          </tr>
                          {isExpanded && packages.length > 0 && (
                            <tr>
                              <td colSpan="4" className="p-0">
                                <div className="bg-muted/20 border-t border-border overflow-x-auto">
                                  <table className="w-full text-xs min-w-[480px]">
                                    <thead>
                                      <tr className="text-muted-foreground">
                                        <th className="text-left px-4 py-1.5 font-medium">OCE</th>
                                        <th className="text-left px-4 py-1.5 font-medium">{language === 'en' ? 'Sender' : 'Expéditeur'}</th>
                                        <th className="text-left px-4 py-1.5 font-medium">{language === 'en' ? 'Recipient' : 'Destinataire'}</th>
                                        <th className="text-left px-4 py-1.5 font-medium">{language === 'en' ? 'Destination' : 'Destination'}</th>
                                        <th className="text-right px-4 py-1.5 font-medium">{language === 'en' ? 'Price' : 'Prix'}</th>
                                        <th className="text-center px-4 py-1.5 font-medium">{language === 'en' ? 'Status' : 'Statut'}</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border/50">
                                      {packages.map((pkg, i) => (
                                        <tr key={i} className="hover:bg-muted/30">
                                          <td className="px-4 py-1.5 font-mono font-medium text-foreground">{pkg.oce_code}</td>
                                          <td className="px-4 py-1.5 text-foreground">{pkg.sender_name}</td>
                                          <td className="px-4 py-1.5 text-foreground">{pkg.recipient_name}</td>
                                          <td className="px-4 py-1.5 text-muted-foreground">{pkg.destination_country}{pkg.destination_city ? ` / ${pkg.destination_city}` : ''}</td>
                                          <td className="px-4 py-1.5 text-right font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(pkg.price)}</td>
                                          <td className="px-4 py-1.5 text-center">
                                            <Badge variant="outline" className={`text-[10px] ${pkg.payment_status === 'paid' ? 'border-green-300 text-green-700' : pkg.payment_status === 'advance' ? 'border-orange-300 text-orange-700' : 'border-red-300 text-red-700'}`}>
                                              {pkg.payment_status === 'paid' ? (language === 'en' ? 'Paid' : 'Payé') : pkg.payment_status === 'advance' ? (language === 'en' ? 'Advance' : 'Acompte') : (language === 'en' ? 'Unpaid' : 'Impayé')}
                                            </Badge>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-muted/30">
                      <td className="px-4 py-2.5 font-semibold text-foreground">Total</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground">{data.total_packages}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(data.total_revenue)}</td>
                      <td className="px-4 py-2.5 text-right"><Badge variant="outline" className="text-[10px]">100%</Badge></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Payment status mini-bar */}
            {statusData.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3">
                  {language === 'en' ? 'Payment status' : 'Statut de paiement'}
                </h4>
                <div className="flex h-4 rounded-full overflow-hidden gap-0.5">
                  {statusData.map((s, i) => (
                    <div
                      key={i}
                      className="transition-all"
                      style={{
                        width: `${(s.value / data.total_packages) * 100}%`,
                        background: s.color,
                      }}
                      title={`${s.name}: ${s.value}`}
                    />
                  ))}
                </div>
                <div className="flex gap-4 mt-2">
                  {statusData.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                      <span className="text-muted-foreground">{s.name}</span>
                      <span className="font-medium text-foreground">{s.value} ({data.total_packages > 0 ? ((s.value / data.total_packages) * 100).toFixed(0) : 0}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Collection by location */}
            {data.by_location && Object.keys(data.by_location).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <MapPin size={16} />
                  {language === 'en' ? 'Collection by location' : 'Collecte par lieu'}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {Object.entries(data.by_location).map(([loc, val]) => {
                    const locLabel = loc === 'origin' ? (language === 'en' ? 'At origin' : 'Au départ')
                      : loc === 'destination' ? (language === 'en' ? 'At destination' : 'À destination')
                      : (language === 'en' ? 'Not specified' : 'Non renseigné');
                    const pct = data.total_revenue > 0 ? ((val.total_amount / data.total_revenue) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={loc} className="border border-border rounded-lg p-3">
                        <div className="text-xs text-muted-foreground">{locLabel}</div>
                        <div className="text-lg font-bold text-foreground mt-1">{fmt(val.total_amount)}</div>
                        <div className="text-xs text-muted-foreground">{val.count} {language === 'en' ? 'packages' : 'colis'} — {pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Collection by partner */}
            {data.by_partner && Object.keys(data.by_partner).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Users size={16} />
                  {language === 'en' ? 'Collection by partner' : 'Collecte par partenaire'}
                </h4>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Partner' : 'Partenaire'}</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Packages' : 'Colis'}</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Collected' : 'Collecté'}</th>
                        <th className="text-center px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Paid/Unpaid' : 'Payé/Impayé'}</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Methods' : 'Moyens'}</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Location' : 'Lieu'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {Object.entries(data.by_partner)
                        .sort(([, a], [, b]) => b.total_amount - a.total_amount)
                        .map(([pid, p]) => (
                        <tr key={pid} className="hover:bg-muted/30">
                          <td className="px-4 py-2.5 font-medium text-foreground">{p.name}</td>
                          <td className="px-4 py-2.5 text-right text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{p.count}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(p.total_amount)}</td>
                          <td className="px-4 py-2.5 text-center">
                            <span className="text-green-600 font-medium">{p.paid}</span>
                            <span className="text-muted-foreground mx-1">/</span>
                            <span className="text-red-600 font-medium">{p.unpaid}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(p.by_method || {}).map(([m, v]) => (
                                <span key={m} className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded">
                                  {labels[m] || m}: {fmt(v.total_amount)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(p.by_location || {}).map(([loc, v]) => {
                                const l = loc === 'origin' ? (language === 'en' ? 'Origin' : 'Départ')
                                  : loc === 'destination' ? (language === 'en' ? 'Dest.' : 'Dest.')
                                  : '—';
                                return (
                                  <span key={loc} className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded">
                                    {l}: {fmt(v.total_amount)}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Money collected by collaborators (who physically received money) */}
            {data.by_collector && Object.keys(data.by_collector).length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Wallet size={16} />
                  {language === 'en' ? 'Money collected by collaborators' : 'Argent encaissé par collaborateur'}
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  {language === 'en'
                    ? 'Amounts physically collected by each collaborator — use this to reconcile and reclaim funds.'
                    : 'Montants physiquement encaissés par chaque collaborateur — pour savoir combien réclamer.'}
                </p>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Collaborator' : 'Collaborateur'}</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Role' : 'Rôle'}</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Transactions' : 'Transactions'}</th>
                        <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'Total collected' : 'Total encaissé'}</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">{language === 'en' ? 'By method' : 'Par moyen'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {Object.entries(data.by_collector)
                        .sort(([, a], [, b]) => b.total_collected - a.total_collected)
                        .map(([cid, c]) => {
                          const roleLabel = c.role === 'local_partner' ? (language === 'en' ? 'Delivery' : 'Livreur')
                            : c.role === 'partner' ? (language === 'en' ? 'Collector' : 'Collecteur')
                            : c.role || '—';
                          return (
                            <tr key={cid} className="hover:bg-muted/30">
                              <td className="px-4 py-2.5 font-medium text-foreground">{c.name}</td>
                              <td className="px-4 py-2.5">
                                <Badge variant="outline" className="text-[10px]">{roleLabel}</Badge>
                              </td>
                              <td className="px-4 py-2.5 text-right text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.count}</td>
                              <td className="px-4 py-2.5 text-right font-bold text-orange-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(c.total_collected)}</td>
                              <td className="px-4 py-2.5">
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(c.by_method || {}).map(([m, v]) => (
                                    <span key={m} className="inline-flex items-center text-[10px] bg-muted px-1.5 py-0.5 rounded">
                                      {labels[m] || m}: {fmt(v.total_amount)}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border bg-orange-50">
                        <td colSpan="3" className="px-4 py-2.5 font-semibold text-foreground">
                          {language === 'en' ? 'Total to reclaim' : 'Total à réclamer'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-bold text-orange-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(Object.values(data.by_collector).reduce((sum, c) => sum + c.total_collected, 0))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentMethodChart;
